import React, { type FormEvent, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import type { CLOTHESTYPE } from '../../../../enums';
import { ClothingService, FireDepartmentService, FirefighterService } from '../../../../services';
import { useAppSelector } from '../../../../store/hooks';
import { selectDepartment, selectIsSuperAdmin } from '../../../../store/reducers';
import type { FireDepartment, Firefighter } from '../../../../types';
import { SimpleBox } from '../../../atoms';

export function AddClothingItem(): React.JSX.Element {
  const userDepartment = useAppSelector(selectDepartment);
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);

  const [code, setCode] = React.useState<string>('');
  const [type, setType] = React.useState<CLOTHESTYPE | 'undefined'>('undefined');
  const [name, setName] = React.useState<string>('');
  const [departments, setDepartments] = React.useState<FireDepartment[]>([]);
  const [selectedDepartment, setSelectedDepartment] = React.useState<string>('undefined');
  const [members, setMembers] = React.useState<Firefighter[]>([]);
  const [selectedMember, setSelectedMember] = React.useState<string>('undefined');

  const handleAddClothingItem = async (): Promise<void> => {
    if (code === '' || name === '' || type === 'undefined' || (isSuperAdmin && selectedDepartment === 'undefined')) {
      toast.warning('Nicht alle Felder sind ausgefüllt ‼️');

      return;
    }

    if (code !== '' && name !== '') {
      const addClothingItemResponse = await ClothingService.addClothingItem({
        code,
        type,
        name,
        department: userDepartment ? userDepartment.id : selectedDepartment,
        firefighter: selectedMember !== 'undefined' ? selectedMember : undefined,
      });

      if (addClothingItemResponse.error) {
        toast.error('Fehler beim Erstellen des neuen Kleidungstückes 😩');

        return;
      }

      setCode('');
      setName('');
      setType('undefined');
      setSelectedMember('undefined');

      toast.success('Kleidungsstück hinzugefügt ✅');
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
  };

  const handleKeyPress = (key: string): void => {
    if (key === 'Enter') {
      handleAddClothingItem();
    }
  };

  const loadFireDepartments = async (): Promise<void> => {
    const fireDepartmentResponse = await FireDepartmentService.getAllFireDepartments();
    if (fireDepartmentResponse.error) {
      toast.error('Fehler beim Laden der Feuerwehren 😩');
      console.log(fireDepartmentResponse.message);

      return;
    }
    setDepartments(fireDepartmentResponse.data as FireDepartment[]);
  };

  const loadFirefighters = async (firedepartment: string): Promise<void> => {
    const firefighterResponse = await FirefighterService.getAllFirefightersOfDepartment(firedepartment);
    if (firefighterResponse.error) {
      toast.error('Fehler beim Laden der Mitglieder 😩');
      console.log(firefighterResponse.message);

      return;
    }
    setMembers(firefighterResponse.data as Firefighter[]);
  };

  useEffect(() => {
    if (isSuperAdmin) {
      loadFireDepartments().then(() => {});
    }
    if (!isSuperAdmin && userDepartment) {
      loadFirefighters(userDepartment.id).then(() => {});
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      loadFirefighters(selectedDepartment).then(() => {});
    }
  }, [selectedDepartment]);

  return (
    <div className='container mt-10'>
      <div className='row'>
        <div className='col-12'>
          <SimpleBox>
            <h4>Kleidungsstück hinzufügen</h4>
            <Form onSubmit={handleSubmit}>
              {isSuperAdmin && departments.length > 0 && (
                <Form.Group className='mt-4 mb-3' controlId='formGroupDepartment'>
                  <div className='row'>
                    <div className='col-12'>
                      <Form.Select
                        value={selectedDepartment}
                        onChange={(e) => setSelectedDepartment(e.target.value as string)}
                      >
                        <option value='undefined'>Abteilung auswählen</option>
                        {departments.map((fd) => (
                          <option value={fd.id}>{fd.name}</option>
                        ))}
                      </Form.Select>
                    </div>
                  </div>
                </Form.Group>
              )}
              <Form.Group className='mt-4 mb-3' controlId='formGroupCode'>
                <div className='row'>
                  <div className='col-12'>
                    <Form.Control
                      type='text'
                      placeholder='Code'
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      onKeyDown={(e) => handleKeyPress(e.key)}
                    ></Form.Control>
                  </div>
                </div>
              </Form.Group>
              <Form.Group className='mb-3' controlId='formGroupNameType'>
                <div className='row'>
                  <div className='col-sm-12 col-md-6'>
                    <Form.Select
                      value={type}
                      onChange={(e) => setType(e.target.value as unknown as CLOTHESTYPE | 'undefined')}
                    >
                      <option value='undefined'>Typ auswählen</option>
                      <option value='0'>Jacke</option>
                      <option value='1'>AGT-Hose</option>
                      <option value='2'>Bundhose</option>
                      <option value='3'>Latzhose</option>
                    </Form.Select>
                  </div>
                  <div className='col-sm-12 col-md-6 mt-3 mt-sm-3 mt-md-0'>
                    <Form.Control
                      type='text'
                      placeholder='Name'
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onKeyDown={(e) => handleKeyPress(e.key)}
                    ></Form.Control>
                  </div>
                </div>
              </Form.Group>
              {members.length > 0 && (
                <Form.Group className='mt-4 mb-3' controlId='formGroupDepartment'>
                  <div className='row'>
                    <div className='col-12'>
                      <Form.Select value={selectedMember} onChange={(e) => setSelectedMember(e.target.value as string)}>
                        <option value='undefined'>Mitglied auswählen (optional)</option>
                        {members.map((fm) => (
                          <option value={fm.id}>
                            {fm.firstname} {fm.lastname}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  </div>
                </Form.Group>
              )}
              <Form.Group controlId='formGroupAdd'>
                <div className='row'>
                  <div className='col-12'>
                    <Button onClick={() => handleAddClothingItem()}>Hinzufügen</Button>
                  </div>
                </div>
              </Form.Group>
            </Form>
          </SimpleBox>
        </div>
      </div>
    </div>
  );
}
